import { useMemo, useState } from 'react';

import { Activity, AlertTriangle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';

import { formatarDataHora, formatarRelativo } from '@/nucleo/formato/datas';
import { cn } from '@/nucleo/utilitarios';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';

import { formatarDuracao, useTrabalhosAgendados, type TrabalhoAgendado } from './dados';

/**
 * `/admin/configuracoes/agendamentos` — a saúde das tarefas
 * automáticas: quando cada uma rodou, quando roda de novo, e quantas falharam.
 *
 * É tela de LEITURA, e só. O protótipo também não oferecia disparar nem pausar
 * trabalho pela interface, então não há aqui nenhum botão que precise mentir —
 * "Atualizar" recarrega a consulta de verdade.
 *
 * Não existe tabela de agendamento neste repositório: o agendador ainda não foi
 * construído. O contrato que a tela espera está em `dados.ts`
 * (`TrabalhoAgendado`), registrado para quem for implementá-lo.
 */
export function TrabalhosAgendadosPage() {
  const { data: trabalhos, isLoading, isError, isFetching, refetch } = useTrabalhosAgendados();
  const [selecionado, setSelecionado] = useState<TrabalhoAgendado | null>(null);

  const totais = useMemo(() => {
    const lista = trabalhos ?? [];
    return {
      total: lista.length,
      comSucesso: lista.filter((item) => item.ultima_situacao === 'sucesso').length,
      comFalha: lista.filter((item) => item.falhas_24h > 0).length,
    };
  }, [trabalhos]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Trabalhos agendados"
        description="Saúde das tarefas automáticas do sistema: alertas de vencimento, escalonamento de apontamento, fechamento mensal e sincronizações."
      >
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw
            className={cn('mr-1.5 h-4 w-4', isFetching && 'animate-spin')}
            aria-hidden="true"
          />
          Atualizar
        </Button>
      </PageHeader>

      {isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar os trabalhos agendados agora. Verifique sua conexão e tente novamente."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <Spinner />
      ) : !trabalhos || trabalhos.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Nenhum trabalho agendado"
          description="Quando houver tarefa automática configurada, a saúde de cada uma aparece aqui."
        />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Resumo rotulo="Trabalhos" valor={totais.total} />
            <Resumo rotulo="Última execução com sucesso" valor={totais.comSucesso} tom="sucesso" />
            <Resumo
              rotulo="Com falha nas últimas 24h"
              valor={totais.comFalha}
              tom={totais.comFalha > 0 ? 'falha' : undefined}
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            {trabalhos.map((trabalho) => (
              <CartaoDoTrabalho
                key={trabalho.id}
                trabalho={trabalho}
                aoAbrir={() => setSelecionado(trabalho)}
              />
            ))}
          </div>
        </>
      )}

      {selecionado && (
        <DetalheDoTrabalho trabalho={selecionado} onFechar={() => setSelecionado(null)} />
      )}
    </div>
  );
}

interface ResumoProps {
  rotulo: string;
  valor: number;
  tom?: 'sucesso' | 'falha';
}

function Resumo({ rotulo, valor, tom }: ResumoProps) {
  return (
    <Card className={cn(tom === 'falha' && 'border-destructive/50')}>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{rotulo}</div>
        <div
          className={cn(
            'text-2xl font-semibold',
            tom === 'sucesso' && 'text-success',
            tom === 'falha' && 'text-destructive',
          )}
        >
          {valor}
        </div>
      </CardContent>
    </Card>
  );
}

interface SeloDeSituacaoProps {
  situacao: TrabalhoAgendado['ultima_situacao'];
}

function SeloDeSituacao({ situacao }: SeloDeSituacaoProps) {
  if (situacao === null) {
    return <Badge variant="outline">Nunca executou</Badge>;
  }
  const deuCerto = situacao === 'sucesso';
  return (
    <Badge variant={deuCerto ? 'default' : 'destructive'} className="gap-1">
      {deuCerto ? (
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
      ) : (
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      )}
      {deuCerto ? 'Sucesso' : 'Falha'}
    </Badge>
  );
}

interface CartaoDoTrabalhoProps {
  trabalho: TrabalhoAgendado;
  aoAbrir: () => void;
}

function CartaoDoTrabalho({ trabalho, aoAbrir }: CartaoDoTrabalhoProps) {
  const temFalha = trabalho.falhas_24h > 0;

  return (
    <Card className={cn('transition-shadow', temFalha && 'border-destructive/50')}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={aoAbrir}
                className="truncate text-sm font-semibold hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              >
                {trabalho.nome}
              </button>
              {!trabalho.ativo && <Badge variant="outline">Inativo</Badge>}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{trabalho.agenda_descrita}</p>
          </div>
          <SeloDeSituacao situacao={trabalho.ultima_situacao} />
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="h-3 w-3" aria-hidden="true" /> Última execução
            </div>
            <div className="font-medium">{formatarRelativo(trabalho.ultima_execucao)}</div>
            <div className="text-muted-foreground">
              {formatarDataHora(trabalho.ultima_execucao)}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Activity className="h-3 w-3" aria-hidden="true" /> Próxima execução
            </div>
            <div className="font-medium">{formatarRelativo(trabalho.proxima_execucao)}</div>
            <div className="text-muted-foreground">
              {formatarDataHora(trabalho.proxima_execucao)}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t pt-2 text-xs">
          <span className="text-muted-foreground">Últimas 24 horas:</span>
          <Badge variant="outline" className="gap-1">
            <CheckCircle2 className="h-3 w-3 text-success" aria-hidden="true" />
            {trabalho.sucessos_24h} com sucesso
          </Badge>
          <Badge variant={temFalha ? 'destructive' : 'outline'} className="gap-1">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {trabalho.falhas_24h} com falha
          </Badge>
          <span className="ml-auto text-muted-foreground">
            Duração: {formatarDuracao(trabalho.duracao_ms)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

interface DetalheDoTrabalhoProps {
  trabalho: TrabalhoAgendado;
  onFechar: () => void;
}

function DetalheDoTrabalho({ trabalho, onFechar }: DetalheDoTrabalhoProps) {
  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && onFechar()}
      title={trabalho.nome}
      description={trabalho.agenda_descrita}
      desktopMaxWidth="max-w-lg"
      footer={
        <Button variant="outline" onClick={onFechar}>
          Fechar
        </Button>
      }
    >
      <dl className="grid gap-3 py-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted-foreground">Expressão de agendamento</dt>
          <dd className="font-mono">{trabalho.agenda}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Situação do trabalho</dt>
          <dd>{trabalho.ativo ? 'Ativo' : 'Inativo'}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Última execução</dt>
          <dd>{formatarDataHora(trabalho.ultima_execucao)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Próxima execução</dt>
          <dd>{formatarDataHora(trabalho.proxima_execucao)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Duração da última</dt>
          <dd>{formatarDuracao(trabalho.duracao_ms)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Resultado da última</dt>
          <dd>
            <SeloDeSituacao situacao={trabalho.ultima_situacao} />
          </dd>
        </div>
      </dl>

      <p className="pt-2 text-xs text-muted-foreground">
        O histórico de execuções aparecerá aqui quando o agendador for construído — hoje o sistema
        ainda não registra cada corrida.
      </p>
    </ResponsiveModal>
  );
}

export default TrabalhosAgendadosPage;
